import { defineModule, type ModuleRoute, type PanelSpec } from "@motolynk/app-core";

import en from "./i18n/en.json";

/** Pages every panel shares: login flows + own profile & security. */
const authRoutes: ModuleRoute[] = [
  { path: "login", layout: "auth", guestOnly: true, page: () => import("./auth/LoginPage") },
  { path: "login/2fa", layout: "auth", page: () => import("./auth/TwoFactorPage") },
  { path: "forgot-password", layout: "auth", page: () => import("./auth/ForgotPasswordPage") },
  // Paths below are the ones Django emails link to (HEADLESS_FRONTEND_URLS)
  { path: "reset-password/:key", layout: "auth", page: () => import("./auth/ResetPasswordPage") },
  { path: "verify-email/:key", layout: "auth", page: () => import("./auth/VerifyEmailPage") },
];

const accountRoutes: ModuleRoute[] = [
  { path: "account/profile", page: () => import("./account/ProfilePage") },
  { path: "account/security", page: () => import("./account/SecurityPage") },
];

const userMenu: PanelSpec["userMenu"] = [
  { id: "profile", label: "accounts:menu.profile", icon: "user", to: "/account/profile" },
  { id: "security", label: "accounts:menu.security", icon: "shield", to: "/account/security" },
];

/** Admin panel only: the team and its roles (journey admin step 18). */
const teamRoutes: ModuleRoute[] = [
  { path: "team", permission: "accounts.view_user", page: () => import("./admin/TeamPage") },
  { path: "roles", permission: "auth.view_group", page: () => import("./admin/RolesPage") },
  { path: "roles/new", permission: "auth.add_group", page: () => import("./admin/RoleEditorPage") },
  {
    path: "roles/:uid",
    permission: "auth.view_group",
    page: () => import("./admin/RoleEditorPage"),
  },
];

/** Admin panel only: customers (journey admin step 10). */
const customerRoutes: ModuleRoute[] = [
  {
    path: "customers",
    permission: "accounts.view_customer",
    page: () => import("./admin/customers/CustomersPage"),
  },
  {
    path: "customers/:uid",
    permission: "accounts.view_customer",
    page: () => import("./admin/customers/CustomerDetailPage"),
  },
];

const teamNav: PanelSpec["nav"] = [
  {
    id: "customers",
    group: "business",
    label: "accounts:nav.customers",
    icon: "user",
    to: "/customers",
    permission: "accounts.view_customer",
    order: 4,
  },
  {
    id: "team",
    group: "usersSecurity",
    label: "accounts:nav.team",
    icon: "users",
    to: "/team",
    permission: "accounts.view_user",
    order: 1,
  },
  {
    id: "roles",
    group: "usersSecurity",
    label: "accounts:nav.roles",
    icon: "shield",
    to: "/roles",
    permission: "auth.view_group",
    order: 2,
  },
];

export const accountsModule = defineModule({
  name: "accounts",
  label: "Accounts & authentication",
  requires: ["core"],
  i18n: { en },
  admin: {
    routes: [...authRoutes, ...accountRoutes, ...teamRoutes, ...customerRoutes],
    nav: teamNav,
    userMenu,
  },
  vendor: {
    routes: [
      ...authRoutes,
      { path: "signup", layout: "auth", guestOnly: true, page: () => import("./auth/SignupPage") },
      ...accountRoutes,
    ],
    userMenu,
  },
});
